/**
 * ANANT TRADING EDUCATION HUB + AI Tutor
 * Route: /education
 */
import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'sonner';
import {
  BookOpen, GraduationCap, Send, Bot, User as UserIcon,
  CheckCircle2, Clock, ChevronRight, ArrowLeft, RefreshCw, Brain
} from 'lucide-react';
import api from '../../utils/api';

const Education = () => {
  const [categories, setCategories] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [progress, setProgress] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTutor, setShowTutor] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [c, l, p] = await Promise.all([
        api.get('/education/categories'),
        api.get('/education/lessons'),
        api.get('/education/my-progress'),
      ]);
      setCategories(c.data.categories || []);
      setLessons(l.data.lessons || []);
      setProgress(p.data);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const openLesson = async (idOrSlug) => {
    try {
      const r = await api.get(`/education/lessons/${idOrSlug}`);
      setSelectedLesson(r.data);
    } catch (e) { toast.error('Lesson not found'); }
  };

  const markComplete = async () => {
    if (!selectedLesson) return;
    try {
      await api.post(`/education/lessons/${selectedLesson.id}/complete`);
      toast.success('Lesson marked complete ✓');
      loadAll();
    } catch (e) { toast.error('Could not save'); }
  };

  const filteredLessons = activeCategory === 'all'
    ? lessons
    : lessons.filter(l => l.category === activeCategory);

  if (loading) return <div className="min-h-screen pt-28 flex items-center justify-center bg-gradient-to-br from-indigo-950 via-slate-950 to-purple-950"><div className="animate-spin w-10 h-10 border-4 border-indigo-400 border-t-transparent rounded-full" /></div>;

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 bg-gradient-to-br from-indigo-950 via-slate-950 to-purple-950 text-white" data-testid="education-page">
      <div className="max-w-7xl mx-auto">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-2xl border border-indigo-400/40 bg-gradient-to-br from-indigo-950/60 via-violet-950/40 to-purple-950/60 p-6 mb-6 backdrop-blur">
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-indigo-500/30 blur-3xl animate-pulse" />
          <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <div className="text-indigo-300 text-xs uppercase tracking-widest font-bold flex items-center gap-1"><GraduationCap className="w-4 h-4" /> Trading Education Hub</div>
              <h1 className="text-3xl sm:text-4xl font-extrabold bg-gradient-to-r from-indigo-300 via-violet-300 to-pink-300 bg-clip-text text-transparent mt-1">Learn. Practice. Profit.</h1>
              <p className="text-slate-300 text-sm mt-2 max-w-xl">From candlesticks to algos — structured lessons + AI Tutor in Hindi/Hinglish. Free for all subscribers.</p>
            </div>
            {progress && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-4 py-3">
                <div className="text-xs text-emerald-300 uppercase tracking-wider">Your Progress</div>
                <div className="text-2xl font-extrabold text-emerald-200">{progress.completed}/{progress.total_lessons}</div>
                <div className="w-32 h-1.5 bg-emerald-900/50 rounded mt-1 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-emerald-400 to-teal-400" style={{ width: `${progress.progress_pct}%` }} />
                </div>
                <div className="text-[10px] text-emerald-300/70 mt-1">{progress.progress_pct}% complete</div>
              </div>
            )}
          </div>
        </div>

        {/* AI Tutor floating button */}
        <button
          onClick={() => setShowTutor(true)}
          data-testid="open-ai-tutor-btn"
          className="fixed bottom-6 right-6 z-[60] px-4 py-3 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-bold shadow-2xl shadow-indigo-500/50 flex items-center gap-2 hover:scale-105 transition"
        >
          <Bot className="w-5 h-5" /> Ask AI Tutor
        </button>

        {!selectedLesson ? (
          <>
            {/* Categories */}
            <div className="flex gap-2 mb-5 overflow-x-auto pb-2">
              <button onClick={() => setActiveCategory('all')} data-testid="cat-all" className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold ${activeCategory === 'all' ? 'bg-indigo-500 text-white' : 'bg-white/5 text-slate-300'}`}>All Lessons</button>
              {categories.map(c => (
                <button key={c.id} onClick={() => setActiveCategory(c.id)} data-testid={`cat-${c.id}`} className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 ${activeCategory === c.id ? `bg-gradient-to-r ${c.color} text-white` : 'bg-white/5 text-slate-300'}`}>
                  <span>{c.icon}</span> {c.name}
                </button>
              ))}
            </div>

            {/* Lessons grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="lessons-grid">
              {filteredLessons.length === 0 && <div className="col-span-full text-center py-12 text-slate-400">No lessons in this category yet.</div>}
              {filteredLessons.map(l => {
                const cat = categories.find(c => c.id === l.category);
                const isDone = progress?.items?.find(p => p.lesson_id === l.id)?.completed;
                return (
                  <button key={l.id} onClick={() => openLesson(l.id)} data-testid={`lesson-card-${l.slug}`} className="text-left bg-white/5 border border-white/10 rounded-2xl p-4 hover:border-indigo-400/50 transition group">
                    <div className="flex items-start justify-between mb-2 gap-2">
                      {cat && (
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-gradient-to-r ${cat.color} text-white`}>
                          {cat.icon} {cat.name}
                        </span>
                      )}
                      {isDone && <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />}
                    </div>
                    <h3 className="text-white font-bold text-base group-hover:text-indigo-300 transition leading-tight">{l.title}</h3>
                    <div className="flex items-center gap-3 mt-3 text-xs text-slate-400">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {l.duration_min} min</span>
                      <span className="capitalize">{l.difficulty}</span>
                    </div>
                    <div className="mt-3 text-indigo-300 text-xs font-semibold flex items-center gap-1">
                      Read lesson <ChevronRight className="w-3 h-3 group-hover:translate-x-1 transition" />
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <LessonView
            lesson={selectedLesson}
            categories={categories}
            onBack={() => setSelectedLesson(null)}
            onComplete={markComplete}
            isDone={progress?.items?.find(p => p.lesson_id === selectedLesson.id)?.completed}
          />
        )}

        {showTutor && <TutorChat onClose={() => setShowTutor(false)} lessonContext={selectedLesson?.id} />}
      </div>
    </div>
  );
};

// ============ Lesson Detail View ============
const LessonView = ({ lesson, categories, onBack, onComplete, isDone }) => {
  const cat = categories.find(c => c.id === lesson.category);
  const html = lesson.content_markdown
    .replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold text-indigo-200 mt-4 mb-3">$1</h1>')
    .replace(/^## (.+)$/gm, '<h2 class="text-xl font-bold text-indigo-300 mt-4 mb-2">$1</h2>')
    .replace(/^### (.+)$/gm, '<h3 class="text-lg font-semibold text-indigo-300 mt-3 mb-2">$1</h3>')
    .replace(/\*\*(.+?)\*\*/g, '<strong class="text-amber-300">$1</strong>')
    .replace(/`(.+?)`/g, '<code class="bg-white/10 px-1.5 py-0.5 rounded text-amber-300 text-sm">$1</code>')
    .replace(/^- (.+)$/gm, '<li class="ml-6 list-disc text-slate-200">$1</li>')
    .replace(/^\d+\. (.+)$/gm, '<li class="ml-6 list-decimal text-slate-200">$1</li>')
    .replace(/\n\n/g, '<br/><br/>');

  return (
    <div data-testid="lesson-view">
      <button onClick={onBack} className="text-indigo-300 mb-4 flex items-center gap-1 hover:underline" data-testid="lesson-back-btn">
        <ArrowLeft className="w-4 h-4" /> Back to lessons
      </button>
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur">
        {cat && <span className={`text-xs font-bold uppercase px-2 py-1 rounded bg-gradient-to-r ${cat.color} text-white inline-block mb-3`}>{cat.icon} {cat.name}</span>}
        <h1 className="text-3xl font-extrabold text-white mb-2">{lesson.title}</h1>
        <div className="flex items-center gap-3 text-sm text-slate-400 mb-4">
          <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {lesson.duration_min} min</span>
          <span className="capitalize">· {lesson.difficulty}</span>
        </div>
        <div className="prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: html }} />
        <div className="mt-6 pt-4 border-t border-white/10 flex justify-between items-center flex-wrap gap-3">
          <button onClick={onBack} className="px-4 py-2 rounded-lg bg-white/5 text-slate-200">← Back</button>
          {isDone ? (
            <div className="text-emerald-300 font-bold flex items-center gap-2"><CheckCircle2 className="w-5 h-5" /> Completed</div>
          ) : (
            <button onClick={onComplete} data-testid="lesson-complete-btn" className="px-6 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Mark Complete
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ AI Tutor Chat ============
const TutorChat = ({ onClose, lessonContext }) => {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Namaste! 🙏 I'm your Anant AI Tutor. Ask me anything about trading — basics, F&O, crypto, risk management, or specific lessons. (English/Hindi/Hinglish OK)" }
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async () => {
    if (!input.trim() || busy) return;
    const userMsg = input.trim();
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setInput('');
    setBusy(true);
    try {
      const r = await api.post('/education/tutor/chat', {
        message: userMsg,
        session_id: sessionId,
        lesson_context_id: lessonContext || null,
      });
      setSessionId(r.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: r.data.reply }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I had a hiccup. Please try again.' }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="ai-tutor-modal">
      <div className="bg-slate-900 border border-indigo-400/40 rounded-2xl sm:max-w-2xl w-full sm:w-auto max-h-[85vh] flex flex-col">
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-indigo-300" />
            <h3 className="font-bold text-white">Anant AI Tutor</h3>
            <span className="text-[10px] bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded uppercase tracking-wide">live</span>
          </div>
          <button onClick={onClose} className="text-slate-400 text-xl" data-testid="tutor-close-btn">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ minHeight: '300px' }}>
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {m.role === 'assistant' && <Bot className="w-6 h-6 text-indigo-300 flex-shrink-0 mt-1" />}
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm whitespace-pre-line ${m.role === 'user' ? 'bg-indigo-500 text-white' : 'bg-white/10 text-slate-100'}`}>
                {m.content}
              </div>
              {m.role === 'user' && <UserIcon className="w-6 h-6 text-indigo-200 flex-shrink-0 mt-1" />}
            </div>
          ))}
          {busy && (
            <div className="flex gap-2">
              <Bot className="w-6 h-6 text-indigo-300" />
              <div className="bg-white/10 rounded-2xl px-3 py-2 text-sm flex gap-1">
                <span className="animate-pulse">●</span><span className="animate-pulse" style={{ animationDelay: '0.2s' }}>●</span><span className="animate-pulse" style={{ animationDelay: '0.4s' }}>●</span>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>
        <div className="p-3 border-t border-white/10 flex gap-2">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') send(); }}
            placeholder="Ask about RSI, F&O, position sizing, anything…"
            data-testid="tutor-input"
            className="flex-1 bg-slate-800 border border-white/10 rounded-xl px-3 py-2 text-sm text-white"
            disabled={busy}
          />
          <button onClick={send} disabled={busy || !input.trim()} data-testid="tutor-send-btn" className="px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-bold disabled:opacity-50 flex items-center gap-1">
            {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Education;
